import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { useToast } from '../ui/UIContext';

interface StoryGeneratorProps {
  /** Absolute https URL of the product image (must be CORS-allowed). */
  imageUrl: string;
  /** Product title — used as the centre headline of the story. */
  title: string;
  /** Pre-formatted price string (e.g. "1 490 MAD"). */
  price: string;
  /** Canonical product URL — rendered at the bottom as a CTA hint. */
  productUrl: string;
}

/**
 * Public-facing Instagram story generator. One click renders a branded
 * 1080×1920 PNG that anyone (customer or seller) can post to their own
 * story without taking the photo out of Fati Caftan's visual identity:
 *
 *   ┌─────────────────────────────┐
 *   │                             │  ← gold "stamp" + watermark band
 *   │   FATI CAFTAN               │
 *   │                             │
 *   │      [ PRODUCT PHOTO ]      │  ← 60% height, object-fit cover
 *   │                             │
 *   │                             │
 *   ├─────────────────────────────┤
 *   │  ── N°… COLLECTION ──       │  ← editorial kicker
 *   │  Caftan vert sauge          │  ← product title (Playfair italic)
 *   │  1 490 MAD                  │  ← price (gold)
 *   │  faticaftan.com             │  ← URL footer
 *   └─────────────────────────────┘
 *
 * All drawing happens client-side via Canvas2D. The image must come from
 * a CORS-permissive origin (Unsplash CDN sets `Access-Control-Allow-Origin:
 * *`; backend-uploaded images are same-origin so they always pass). A
 * cross-origin taint silently breaks `toBlob`, so we surface that path as
 * an explicit error toast instead of a silent failure.
 */
export function StoryGenerator({ imageUrl, title, price, productUrl }: StoryGeneratorProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const [busy, setBusy] = useState(false);

  async function handleDownload() {
    setBusy(true);
    try {
      const blob = await renderStoryBlob({ imageUrl, title, price, productUrl });
      const url = URL.createObjectURL(blob);
      const safeSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'fati-caftan';
      const a = document.createElement('a');
      a.href = url;
      a.download = `fati-caftan-${safeSlug}-story.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after the click so Safari has time to consume the blob URL.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      showToast(t('story.success'), 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'render-failed';
      showToast(`${t('story.error')} (${msg})`, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={busy}
      className="inline-flex items-center gap-2 px-4 py-2 border border-linen text-ink-soft hover:text-brass hover:border-brass transition-colors editorial-label disabled:opacity-60 disabled:cursor-not-allowed"
      aria-label={t('story.button')}
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
      ) : (
        <Download className="w-4 h-4" aria-hidden="true" />
      )}
      <span>{busy ? t('story.generating') : t('story.button')}</span>
    </button>
  );
}

interface RenderArgs {
  imageUrl: string;
  title: string;
  price: string;
  productUrl: string;
}

const WIDTH = 1080;
const HEIGHT = 1920;
const PHOTO_HEIGHT = Math.round(HEIGHT * 0.6); // 1152px — leaves ~768px for the lower brand band
const PADDING_X = 80;

/**
 * Pure render — composes the 1080×1920 canvas and resolves with the PNG
 * blob. Exported indirectly via the component so tests can monkey-patch
 * the underlying Canvas2D mocks.
 */
async function renderStoryBlob(args: RenderArgs): Promise<Blob> {
  const img = await loadImage(args.imageUrl);

  // Canvas2D paints with the system fallback unless the web font is already
  // parsed. Wait for the document fonts so Arabic titles render in El Messiri
  // (and Latin titles in Playfair) instead of a generic system face. Guarded
  // for jsdom, which has no FontFaceSet.
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // Non-fatal — fall through and draw with whatever is loaded.
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas-unsupported');

  // Background — deep noir from the cinematic-maison palette
  ctx.fillStyle = '#0E0B08';
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // Photo region (top). Object-fit: cover (crop to fill).
  drawImageCover(ctx, img, 0, 0, WIDTH, PHOTO_HEIGHT);

  // Soft gradient fade from photo into brand band so the seam is invisible
  const fade = ctx.createLinearGradient(0, PHOTO_HEIGHT - 160, 0, PHOTO_HEIGHT + 40);
  fade.addColorStop(0, 'rgba(14,11,8,0)');
  fade.addColorStop(1, 'rgba(14,11,8,1)');
  ctx.fillStyle = fade;
  ctx.fillRect(0, PHOTO_HEIGHT - 160, WIDTH, 200);

  // Editorial kicker — "FATI CAFTAN" centered above the title
  const kickerY = PHOTO_HEIGHT + 120;
  drawCenteredHairline(ctx, kickerY - 40);
  ctx.fillStyle = '#A89060';
  ctx.font = '500 32px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.letterSpacing = '0.4em';
  ctx.fillText('FATI  CAFTAN', WIDTH / 2, kickerY);
  drawCenteredHairline(ctx, kickerY + 40);

  // Product title — wraps over up to 3 lines. Arabic copy uses El Messiri
  // (Playfair has no Arabic glyphs and would fall back to a system serif);
  // Latin copy keeps the Playfair italic editorial look.
  const titleIsArabic = /[؀-ۿ]/.test(args.title);
  ctx.fillStyle = '#E8DFCE';
  ctx.font = titleIsArabic
    ? '600 80px "El Messiri", "Noto Sans Arabic", sans-serif'
    : 'italic 300 76px "Playfair Display", Georgia, serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const titleY = kickerY + 90;
  drawWrappedText(ctx, args.title, WIDTH / 2, titleY, WIDTH - PADDING_X * 2, 86, 3);

  // Price — gold sans-serif. Tajawal is in the fallback list so the Arabic
  // unit "درهم" renders (Inter covers the Latin digits, Tajawal the word).
  ctx.fillStyle = '#A89060';
  ctx.font = '500 44px Inter, "Tajawal", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(args.price, WIDTH / 2, HEIGHT - 220);

  // Footer — domain only (NOT the full product URL). Instagram story text
  // isn't tappable, so a long /products/<uuid> path is dead weight and
  // overflows the canvas width. The bare domain is readable, on-brand, and
  // always fits; viewers reach the catalog from there (or the seller adds a
  // native IG link sticker per story for a tappable product link).
  ctx.fillStyle = '#B8AC97';
  ctx.font = '400 30px Inter, system-ui, sans-serif';
  ctx.letterSpacing = '0.16em';
  ctx.fillText(domainOnly(args.productUrl), WIDTH / 2, HEIGHT - 120);
  ctx.letterSpacing = '0';

  // Final canvas-wide vignette to keep IG attention on centre
  const vignette = ctx.createRadialGradient(
    WIDTH / 2, HEIGHT / 2, Math.min(WIDTH, HEIGHT) * 0.45,
    WIDTH / 2, HEIGHT / 2, Math.max(WIDTH, HEIGHT) * 0.75,
  );
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.45)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('toBlob-null'));
      },
      'image/png',
      0.95,
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image-load-failed'));
    img.src = src;
  });
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
) {
  const sourceRatio = img.width / img.height;
  const targetRatio = dw / dh;
  let sx = 0;
  let sy = 0;
  let sw = img.width;
  let sh = img.height;
  if (sourceRatio > targetRatio) {
    // Source wider than target — crop sides
    sw = img.height * targetRatio;
    sx = (img.width - sw) / 2;
  } else {
    // Source taller — crop top/bottom
    sh = img.width / targetRatio;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = word;
      if (lines.length >= maxLines) break;
    } else {
      current = candidate;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  if (lines.length === maxLines && current) {
    // Replace last line with ellipsised version if it overflows
    const last = lines[lines.length - 1];
    if (ctx.measureText(last).width > maxWidth) {
      let truncated = last;
      while (truncated.length > 1 && ctx.measureText(truncated + '…').width > maxWidth) {
        truncated = truncated.slice(0, -1);
      }
      lines[lines.length - 1] = truncated + '…';
    }
  }
  lines.forEach((line, i) => ctx.fillText(line, x, y + i * lineHeight));
}

function drawCenteredHairline(ctx: CanvasRenderingContext2D, y: number) {
  const w = 60;
  const x = WIDTH / 2 - w / 2;
  ctx.fillStyle = '#A89060';
  ctx.fillRect(x, y, w, 1);
}

/**
 * Reduce any product URL to its bare host (e.g.
 * "https://faticaftan.com/products/<uuid>" → "faticaftan.com"). Falls back
 * to a scheme/path strip if the URL can't be parsed (e.g. a relative path).
 */
function domainOnly(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0];
  }
}
